/**
 * Telling an AUTH failure apart from a TRANSPORT failure.
 *
 * WHY THIS EXISTS. When silent token acquisition fails, the interceptor refuses
 * to send the request and synthesises an HttpErrorResponse with `status: 0` —
 * because no HTTP call happened. But every classifier in this app reads status 0
 * as "no response came back", i.e. offline. So an expired session was reported
 * as a network problem, and the UI offered "Try again" — a button that cannot
 * possibly fix an expired token. Joel hit exactly that: "silent token
 * acquisition failed. The request to GetProjectlist was not sent", with Try
 * again as the only option.
 *
 * Status alone cannot carry this distinction, so the refusal carries an explicit
 * marker and everything downstream asks THIS function rather than re-deriving it
 * from a status code.
 */

/** Stamped into the error body of a request refused for want of a token. */
export const AUTH_FAILURE_MARKER = 'vericure.auth.failure';

export interface AuthFailureBody {
  [AUTH_FAILURE_MARKER]: true;
  /** Machine reason: 'no-account' | 'silent-acquisition-failed' | 'unauthorized'. */
  reason: string;
  /** Whether automatic recovery has already been attempted to exhaustion. */
  recoveryExhausted: boolean;
  message: string;
}

export function authFailureBody(
  reason: string,
  message: string,
  recoveryExhausted: boolean
): AuthFailureBody {
  return { [AUTH_FAILURE_MARKER]: true, reason, recoveryExhausted, message };
}

/** True when this failure is about identity, not about the network. */
export function isAuthFailure(error?: unknown): boolean {
  const body = (error as { error?: unknown } | null | undefined)?.error;
  if (body && typeof body === 'object' && AUTH_FAILURE_MARKER in (body as object)) {
    return true;
  }
  // A 401 is an auth verdict wherever it comes from.
  return (error as { status?: number } | null | undefined)?.status === 401;
}

/**
 * True when re-signing-in has already been tried and did not stick.
 *
 * The difference matters in the UI: a recoverable auth failure gets sent
 * straight to sign-in, while an exhausted one must STOP and offer a manual
 * button. Bouncing again would be an infinite redirect loop.
 */
export function isRecoveryExhausted(error?: unknown): boolean {
  const body = (error as { error?: unknown } | null | undefined)?.error as
    | AuthFailureBody
    | undefined;
  return !!body && typeof body === 'object' && body.recoveryExhausted === true;
}
